import { translate } from '@jsverse/transloco';

import { CalculationListItem } from 'app/providers/storage-api/predictive.service';
import { Analysis } from '../../../providers/storage-api/analysis.service';
import { ObjectLabel, ItemLabel } from '../../../providers/storage-api/object-label.service';
import { Project } from '../../../providers/storage-api/project.service';
import { ActiveUse, DisplayItem, ProjectItem } from '../services/project-detail.service';
import { mapObjectLabelsToProjectItemLabels } from './common.model';
import { ProjectCollection } from './project-collection.model';

export class ProjectAnalysis {
  static createAnalysisItem(
    analysis: Analysis,
    projectCollection: ProjectCollection,
    project: Project
  ): ProjectItem {
    if (!projectCollection?.data) {
      // This occurs after a collection has been deleted but the
      // user attempts to open an analysis group that references it before
      // a refresh - vbrazelton
      // DASH-9378 for reference
      return null;
    }

    const displayData: DisplayItem = {
      docCount: analysis.documentCount,
      configName: analysis.configName,
      collectionName:
        project.collections.find((c) => c.collectionName === analysis.collectionName)
          ?.displayName ?? 'N/A',
      activeUse: [],
      isAutoAnalysis: projectCollection.data.isAutoAnalyze,
      origin: projectCollection.data.origin,
    };

    return {
      type: 'analysis',
      id: analysis.name ?? analysis.analysisName,
      name: analysis.displayName,
      modified: new Date(analysis.modifiedAt),
      created: new Date(analysis.createdAt),
      labels: this.createAnalysisLabels(analysis, projectCollection),
      displayData,
      analysis,
      isSocial: analysis.isSocial,
    };
  }

  private static createAnalysisLabels(
    analysis: Analysis,
    projectCollection: ProjectCollection
  ): Partial<ItemLabel>[] {
    const labels = [];

    const aaLabel = ProjectCollection.createAALabel(projectCollection);
    if (aaLabel) {
      labels.push(aaLabel);
    }

    const embeddingLabel = this.createEmbeddingLabel(analysis);
    if (embeddingLabel) {
      labels.push(embeddingLabel);
    }

    labels.push(...mapObjectLabelsToProjectItemLabels(analysis.objectLabels as ObjectLabel[]));

    return labels;
  }

  private static createEmbeddingLabel(analysis: Analysis) {
    switch (analysis.embeddingStatus) {
      case 'FINISHED':
        return {
          label: 'EMB',
          tooltip: translate('project-collection-model.embedding', {
            strategy: analysis.embeddingStrategy,
            model: analysis.embeddingModel,
          }),
          type: 'system',
        };
      case 'FINAL_ERROR':
        return {
          label: 'EMB_ERR',
          tooltip: translate('project-collection-model.embedding-error'),
          type: 'system',
        };
      case 'TOO_MANY_CONTINUES':
        return {
          label: 'EMB_UNF',
          tooltip: translate('project-collection-model.embedding-unfinished', {
            analysisCount: analysis.analysisCount,
            embeddingCount: analysis.embeddingDocumentCount,
          }),
          type: 'system',
        };
      case 'RUNNING':
      case 'ERROR':
      case 'CONTINUE':
        return {
          label: 'EMB_GEN',
          tooltip: translate('project-collection-model.embedding-generating'),
          type: 'system',
        };
    }
  }

  static createKdaAnalysisItem(
    analysisTemplate: Analysis,
    calculation: CalculationListItem,
    projectCollection: ProjectCollection,
    project: Project
  ): ProjectItem {
    const labels = this.createKdaAnalysisLabels(calculation, projectCollection);
    const displayData: DisplayItem = {
      docCount: calculation.documentCount,
      configName: analysisTemplate.configName,
      collectionName: projectCollection.data.displayName ?? 'N/A',
      isDriverAnalysis: true,
    };
    const kda: ProjectItem = {
      type: 'analysis',
      id: calculation.id,
      name: calculation.displayName,
      modified: new Date(calculation.finishedAt),
      created: new Date(calculation.createdAt),
      labels,
      displayData,
      calculation,
    };

    kda.displayData.activeUse = ProjectAnalysis.getKdaActiveUse(project, calculation);

    return kda;
  }

  private static createKdaAnalysisLabels(
    calculation: CalculationListItem,
    projectCollection: ProjectCollection
  ): Partial<ItemLabel>[] {
    const labels = [ProjectCollection.createKDALabel(projectCollection)];

    labels.push(...mapObjectLabelsToProjectItemLabels(calculation.objectLabels as ObjectLabel[]));

    return labels;
  }

  static getKdaActiveUse(project: Project, calculation: CalculationListItem) {
    const activeDashboards: Array<ActiveUse> = [];
    const dashboards = project.dashboards;
    for (const dashboard of dashboards) {
      const foundAnalysis = dashboard.widgets.find(
        (widget) => calculation.id === widget.dataSourceId
      );

      if (foundAnalysis) {
        const duplicate = activeDashboards.find((dash) => dash.analysisID === foundAnalysis.id);
        if (!duplicate) {
          activeDashboards.push({
            activeUseType: 'dashboard',
            displayName: dashboard.title,
            analysisID: foundAnalysis.id,
            ...dashboard,
          });
        }
      }
    }
    return activeDashboards.sort((i1, i2) => i1.displayName.localeCompare(i2.displayName));
  }
}
